import { Link } from '@reach/router';
import { Resource, ResourcesApi } from 'api/resourcesApi';
import {
  downloadFileInPlace,
  openFileInNewTab,
} from 'components/lessons/lessonsHelpers';
import React, { RefObject, useState } from 'react';
import EqualHeightsList from './EqualHeightsList';
import {
  playerPath,
  presentationPath,
  resourceTypeIcon,
  ResourceTypeTab,
  withColour,
} from './resourcesHelpers';

type CardsProps = {
  subjectCode: string;
  subjectsId: number;
  /** The cards, in the order they show */
  resources: Resource[];
  colorCSS: string | null;
  /** The page block whose cards decide whether the lists are equalised */
  pageRef: RefObject<HTMLElement>;
  /** The cards centred in their row, as on category and search pages */
  bCentred?: boolean;
  /** Search results: BtnOne buttons and a space between cards */
  bSearchResults?: boolean;
};

/**
 * Resource cards. Open shows a presentation or a video or audio file on its
 * own page and any other file in a new tab; Download saves the file in place.
 */
export const ResourceCards: React.FC<CardsProps> = ({
  subjectCode,
  subjectsId,
  resources,
  colorCSS,
  pageRef,
  bCentred = false,
  bSearchResults = false,
}) => {
  const [bFileError, setBFileError] = useState(false);
  const buttonClass = bSearchResults
    ? 'BtnOne BtnOpen Top'
    : 'BtnTwo BtnOpen Top';

  const fileUrl = (resourcesId: number, bDownload: boolean) => () =>
    ResourcesApi.getFile(subjectsId, resourcesId, bDownload).then((file) =>
      typeof file === 'string' ? null : file.filePath
    );

  const openFile = (event: React.MouseEvent, resourcesId: number) => {
    event.preventDefault();
    setBFileError(false);
    openFileInNewTab(fileUrl(resourcesId, false)).then((bOpened) =>
      setBFileError(!bOpened)
    );
  };

  const downloadFile = (event: React.MouseEvent, resourcesId: number) => {
    event.preventDefault();
    setBFileError(false);
    downloadFileInPlace(fileUrl(resourcesId, true)).then((bSaved) =>
      setBFileError(!bSaved)
    );
  };

  const buttons = (resource: Resource) => {
    if (!resource.bCanOpen) {
      return (
        <div className="BtnDisabled BtnView">
          {/* A link like the other card buttons, which share its styles */}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a aria-disabled="true">Subscribers Only</a>
        </div>
      );
    }

    if (resource.resourceType === 'Presentation') {
      return (
        <div className={buttonClass}>
          <a
            href={presentationPath(subjectCode, resource.resourcesId)}
            target="_blank"
            rel="noreferrer"
          >
            Open
          </a>
        </div>
      );
    }

    const bPlayedInPage =
      resource.resourceType === 'Video' || resource.resourceType === 'Audio';

    return (
      <>
        <div className={buttonClass}>
          <a
            href={playerPath(subjectCode, resource.resourcesId)}
            target="_blank"
            rel="noreferrer"
            onClick={
              bPlayedInPage
                ? undefined
                : (event) => openFile(event, resource.resourcesId)
            }
          >
            Open
          </a>
        </div>
        {resource.bDownloadable && (
          <div className={buttonClass}>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              role="button"
              onClick={(event) => downloadFile(event, resource.resourcesId)}
              onKeyDown={(event) => {
                if (event.key === ' ') {
                  event.preventDefault();
                  event.currentTarget.click();
                }
              }}
            >
              Download
            </a>
          </div>
        )}
      </>
    );
  };

  const card = (resource: Resource) => (
    <div
      className={withColour(
        `Item_R ItemList01${resource.bCanOpen ? '' : ' Disabled'}`,
        colorCSS
      )}
    >
      <div className="Table_R">
        <div className="Img_R">
          <img
            src={resourceTypeIcon(resource.resourceType)}
            alt={resource.name}
          />
        </div>
        <div className="Desc_R TxtList01">
          <div className="VariableText">
            <div className="In">
              <div className="Txt_R">{resource.name}</div>
              <div className="Nbre_R">{resource.resourceType}</div>
            </div>
          </div>
          {buttons(resource)}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="WrapperActivity">
        <EqualHeightsList
          className={`WarpperResource WrapperList01${
            bCentred ? ' CenterBlocks' : ''
          }`}
          cardsSource={resources}
          pageRef={pageRef}
        >
          {resources.map((resource) => (
            <React.Fragment key={resource.resourcesId}>
              {bSearchResults && ' '}
              {card(resource)}
            </React.Fragment>
          ))}
          {bSearchResults && ' '}
        </EqualHeightsList>
      </div>
      {bFileError && (
        <p className="ErrorLabel">An error occurred, please try again.</p>
      )}
    </>
  );
};

type TabsProps = {
  tabs: ResourceTypeTab[];
  activeTab: ResourceTypeTab | null;
  /** The page path the tabs add their type segment to */
  basePath: string;
};

/** The tabs for the resource types a page has, the open one highlighted */
export const ResourceTypeTabs: React.FC<TabsProps> = ({
  tabs,
  activeTab,
  basePath,
}) => (
  <div className="KeyStageChoice Img">
    <div className="Table_KS">
      {tabs.map((tab) => (
        <Link
          key={tab.segment}
          to={`${basePath}/${tab.segment}`}
          className={`Cell_KS ${tab.cellClass}${
            tab === activeTab ? ' Highlight' : ''
          }`}
        >
          <img
            className={
              tab.resourceType === 'Presentation' ? 'InvertImage' : undefined
            }
            src={tab.icon}
            alt={tab.resourceType}
          />
        </Link>
      ))}
    </div>
  </div>
);
